import React, { useState, useRef, ChangeEvent, DragEvent } from 'react';
import {
  Camera,
  Upload,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Trash2,
  Image as ImageIcon,
  RotateCcw,
  Maximize2,
  X,
  ShieldCheck,
  FileCheck2,
  Info,
} from 'lucide-react';
import { Job } from '../../types';
import {
  uploadJobCompletionPhoto,
  professionalMarkJobCompleteApi,
  compressImageFile,
} from '../../lib/supabaseService';

export interface JobCompletionPhotoProps {
  job: Job;
  professionalId: string;
  assignmentId?: string;
  maxSizeKB?: number;
  autoSubmitCompletion?: boolean;
  onPhotoUploaded?: (publicUrl: string) => void;
  onCompletionSubmitted?: (res: { success: boolean; message?: string }) => void;
  onError?: (errorMsg: string) => void;
  className?: string;
}

export const JobCompletionPhoto: React.FC<JobCompletionPhotoProps> = ({
  job,
  professionalId,
  assignmentId,
  maxSizeKB = 200,
  autoSubmitCompletion = false,
  onPhotoUploaded,
  onCompletionSubmitted,
  onError,
  className = '',
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(job.completionPhotoUrl || null);
  const [uploadedPhotoUrl, setUploadedPhotoUrl] = useState<string | null>(job.completionPhotoUrl || null);
  const [completionNotes, setCompletionNotes] = useState<string>(job.completionNotes || '');
  const [uploading, setUploading] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState<boolean>(false);
  const [isModalPreviewOpen, setIsModalPreviewOpen] = useState<boolean>(false);

  const galleryInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const maxSizeBytes = maxSizeKB * 1024;

  const handleFileSelect = async (file: File | null) => {
    setError(null);
    setSuccessMsg(null);

    if (!file) return;

    // 1. Format Validation
    if (!file.type.startsWith('image/')) {
      const err = 'Invalid file format. Please upload a valid image file (JPEG, PNG, or WebP).';
      setError(err);
      if (onError) onError(err);
      return;
    }

    // 2. Auto-compress camera snaps or large files to under maxSizeBytes
    setUploading(true);
    let processedFile = file;
    if (file.size > maxSizeBytes) {
      try {
        processedFile = await compressImageFile(file, maxSizeBytes);
      } catch (compressErr) {
        console.warn('Camera photo compression notice:', compressErr);
      }
    }

    if (processedFile.size > maxSizeBytes) {
      setUploading(false);
      const fileSizeKB = (processedFile.size / 1024).toFixed(1);
      const err = `Photo size (${fileSizeKB}KB) exceeds the max allowed ${maxSizeKB}KB limit. Please choose a clearer compressed photo under ${maxSizeKB}KB.`;
      setError(err);
      if (onError) onError(err);
      return;
    }

    setSelectedFile(processedFile);
    const localPreview = URL.createObjectURL(processedFile);
    setPreviewUrl(localPreview);

    // 3. Upload Completion Photo
    try {
      const publicUrl = await uploadJobCompletionPhoto(processedFile, job.id, professionalId, completionNotes);
      
      setUploadedPhotoUrl(publicUrl);
      setUploading(false);
      setSuccessMsg('Completion photo uploaded and secured.');

      if (onPhotoUploaded) {
        onPhotoUploaded(publicUrl);
      }

      // If autoSubmitCompletion flag is enabled, finalize completion update
      if (autoSubmitCompletion) {
        await executeCompletionSubmit(publicUrl);
      }
    } catch (err: any) {
      setUploading(false);
      const msg = err.message || 'Failed to upload completion photo.';
      setError(msg);
      if (onError) onError(msg);
    }
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    handleFileSelect(file);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    const file = e.dataTransfer.files?.[0] || null;
    handleFileSelect(file);
  };

  const handleClearPhoto = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setUploadedPhotoUrl(null);
    setError(null);
    setSuccessMsg(null);
    if (galleryInputRef.current) galleryInputRef.current.value = '';
    if (cameraInputRef.current) cameraInputRef.current.value = '';
  };

  const executeCompletionSubmit = async (photoUrlToSubmit?: string) => {
    const finalUrl = photoUrlToSubmit || uploadedPhotoUrl || previewUrl;
    setSubmitting(true);
    setError(null);

    try {
      const res = await professionalMarkJobCompleteApi(
        job.id,
        professionalId,
        finalUrl || undefined,
        completionNotes.trim()
      );

      if (res.success) {
        const msg = res.message || 'Job completion submitted successfully! The customer has been notified for confirmation.';
        setSuccessMsg(msg);
        if (onCompletionSubmitted) {
          onCompletionSubmitted({ success: true, message: msg });
        }
      } else {
        const err = res.error || 'Failed to submit job completion.';
        setError(err);
        if (onCompletionSubmitted) {
          onCompletionSubmitted({ success: false, message: err });
        }
      }
    } catch (err: any) {
      const msg = err.message || 'An error occurred submitting job completion.';
      setError(msg);
      if (onError) onError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const fileSizeKB = selectedFile ? (selectedFile.size / 1024).toFixed(1) : '0';

  return (
    <div className={`bg-white rounded-2xl border border-slate-200 shadow-xs p-5 space-y-4 ${className}`}>
      {/* Header Banner */}
      <div className="flex items-start justify-between gap-3 border-b border-slate-100 pb-3">
        <div>
          <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-xs font-bold border border-emerald-200 mb-1">
            <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
            <span>Active Assignment Proof</span>
          </div>
          <h3 className="text-sm font-extrabold text-slate-900">Job Completion Photo Evidence</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Job: <span className="font-semibold text-slate-700">{job.title}</span>
            {assignmentId && <span className="ml-1 text-[11px] text-slate-400 font-mono">(Ref: {assignmentId.slice(0, 8)})</span>}
          </p>
        </div>
        <span className="shrink-0 text-[11px] font-mono font-bold px-2.5 py-1 rounded-lg bg-amber-50 text-amber-900 border border-amber-200">
          Max {maxSizeKB}KB
        </span>
      </div>

      {/* Info Callout */}
      <div className="p-3 bg-blue-50/70 border border-blue-100 rounded-xl flex items-start gap-2.5 text-xs text-blue-900">
        <Info className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
        <p className="leading-relaxed">
          Upload clear photo proof showing the finished work. This evidence is presented directly to the customer to confirm completion and authorise release of held escrow funds.
        </p>
      </div>

      {/* Status Messages */}
      {error && (
        <div className="p-3.5 bg-rose-50 border border-rose-200 rounded-xl flex items-start gap-2.5 text-xs text-rose-800 animate-shake">
          <AlertCircle className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
          <span className="font-medium">{error}</span>
        </div>
      )}

      {successMsg && (
        <div className="p-3.5 bg-emerald-50 border border-emerald-200 rounded-xl flex items-start gap-2.5 text-xs text-emerald-800 animate-fade-in">
          <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
          <span className="font-medium">{successMsg}</span>
        </div>
      )}

      {/* Upload Zone or Photo Preview */}
      {!previewUrl ? (
        <div className="space-y-3">
          {/* Drag and Drop Zone */}
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => !uploading && galleryInputRef.current?.click()}
            className={`border-2 border-dashed rounded-2xl p-6 text-center transition-all cursor-pointer group flex flex-col items-center justify-center min-h-[160px] ${
              isDragOver
                ? 'border-emerald-500 bg-emerald-50/40'
                : 'border-slate-300 hover:border-emerald-500 bg-slate-50 hover:bg-emerald-50/10'
            } ${uploading ? 'opacity-50 pointer-events-none' : ''}`}
          >
            <Upload className="w-8 h-8 text-slate-400 group-hover:text-emerald-600 mb-2 transition-colors" />
            <span className="text-xs font-bold text-slate-800 group-hover:text-emerald-900 block">
              Drag & drop completion photo here, or click to browse
            </span>
            <span className="text-[11px] text-slate-500 block mt-1">
              Supports JPEG, PNG, or WebP (max limit: {maxSizeKB}KB)
            </span>
          </div>

          {/* Action Triggers: Gallery vs Camera */}
          <div className="grid grid-cols-2 gap-2.5">
            <button
              type="button"
              disabled={uploading}
              onClick={() => galleryInputRef.current?.click()}
              className="py-3 px-4 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-800 font-bold text-xs flex items-center justify-center gap-2 cursor-pointer transition-colors shadow-2xs min-h-[44px] disabled:opacity-50"
            >
              <ImageIcon className="w-4 h-4 text-emerald-600 shrink-0" />
              <span>Choose Photo</span>
            </button>

            <button
              type="button"
              disabled={uploading}
              onClick={() => cameraInputRef.current?.click()}
              className="py-3 px-4 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-800 font-bold text-xs flex items-center justify-center gap-2 cursor-pointer transition-colors shadow-2xs min-h-[44px] disabled:opacity-50"
            >
              <Camera className="w-4 h-4 text-blue-600 shrink-0" />
              <span>Take Photo (Camera)</span>
            </button>
          </div>

          {/* Hidden File Inputs */}
          <input
            ref={galleryInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={handleInputChange}
            disabled={uploading}
            className="hidden"
          />
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handleInputChange}
            disabled={uploading}
            className="hidden"
          />
        </div>
      ) : (
        /* Preview Card */
        <div className="space-y-3">
          <div className="relative rounded-2xl overflow-hidden border border-slate-200 bg-slate-950 group">
            <img
              src={previewUrl}
              alt="Job Completion Evidence"
              className="w-full h-52 sm:h-64 object-cover"
            />

            {/* Upload Overlay Spinner */}
            {uploading && (
              <div className="absolute inset-0 bg-slate-950/80 backdrop-blur-2xs flex flex-col items-center justify-center text-white gap-2">
                <Loader2 className="w-7 h-7 animate-spin text-emerald-400" />
                <span className="text-xs font-bold">Securing Photo to Assignment Record...</span>
              </div>
            )}

            {/* Control Bar Top Right */}
            {!uploading && (
              <div className="absolute top-3 right-3 flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setIsModalPreviewOpen(true)}
                  className="p-2 bg-slate-900/80 hover:bg-slate-900 text-white rounded-full shadow-md transition-all cursor-pointer hover:scale-105"
                  title="Expand view"
                >
                  <Maximize2 className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => cameraInputRef.current?.click()}
                  className="p-2 bg-slate-900/80 hover:bg-slate-900 text-white rounded-full shadow-md transition-all cursor-pointer hover:scale-105"
                  title="Retake with camera"
                >
                  <RotateCcw className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={handleClearPhoto}
                  className="p-2 bg-rose-600/90 hover:bg-rose-700 text-white rounded-full shadow-md transition-all cursor-pointer hover:scale-105"
                  title="Remove photo"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            )}

            {/* Bottom Metadata Ribbon */}
            <div className="absolute bottom-0 inset-x-0 p-3 bg-slate-950/85 text-white flex items-center justify-between text-xs">
              <div className="flex items-center gap-2 truncate max-w-[65%]">
                <FileCheck2 className="w-4 h-4 text-emerald-400 shrink-0" />
                <span className="truncate text-slate-200 font-medium">
                  {selectedFile ? selectedFile.name : 'Completion_Proof.jpg'}
                </span>
              </div>

              {selectedFile && (
                <span className="font-mono font-bold text-emerald-400 text-xs">
                  {fileSizeKB} KB / {maxSizeKB} KB
                </span>
              )}

              {uploadedPhotoUrl && !selectedFile && (
                <span className="inline-flex items-center gap-1.5 text-emerald-400 font-bold">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  <span>Verified & Linked</span>
                </span>
              )}
            </div>
          </div>

          {/* Optional Completion Notes */}
          {!autoSubmitCompletion && (
            <div className="space-y-1.5 pt-1">
              <label className="block text-xs font-bold text-slate-800">
                Completion Summary / Work Notes <span className="text-slate-400 font-normal">(Optional)</span>
              </label>
              <textarea
                value={completionNotes}
                onChange={(e) => setCompletionNotes(e.target.value)}
                placeholder="e.g., Finished repair, tested water flow, cleared site debris."
                rows={2}
                disabled={submitting}
                className="w-full p-3 text-xs border border-slate-200 rounded-xl focus:outline-hidden focus:border-emerald-500 text-slate-800"
              />
            </div>
          )}

          {/* Submit Completion Button if not auto-submitted */}
          {!autoSubmitCompletion && (
            <button
              type="button"
              disabled={submitting || uploading}
              onClick={() => executeCompletionSubmit()}
              className="w-full py-3 px-4 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-black text-xs shadow-md transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50 min-h-[44px]"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Submitting Evidence to Customer...</span>
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4" />
                  <span>Upload Completion Photo</span>
                </>
              )}
            </button>
          )}
        </div>
      )}

      {/* Expanded Modal Preview */}
      {isModalPreviewOpen && previewUrl && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/90 backdrop-blur-xs animate-fade-in">
          <div className="relative max-w-3xl w-full bg-slate-900 rounded-2xl overflow-hidden border border-slate-800 shadow-2xl p-2 flex flex-col items-center">
            <button
              type="button"
              onClick={() => setIsModalPreviewOpen(false)}
              className="absolute top-4 right-4 z-10 p-2 bg-slate-800 hover:bg-slate-700 text-white rounded-full transition-colors cursor-pointer"
            >
              <X className="w-5 h-5" />
            </button>
            <img
              src={previewUrl}
              alt="Full Resolution Completion Evidence"
              className="max-h-[80vh] w-auto object-contain rounded-xl"
            />
            <div className="w-full p-3 text-center text-xs text-slate-300 font-medium">
              Job Proof: {job.title} &bull; {job.lga}, {job.state}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
